import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ArrowRight } from "lucide-react";
import { useCart } from "@/lib/cart";
import type { FlightResult, Flight, MultiSliceFlight, FareOption, FlightDetails, FlightSlice, FlightSegment, ItineraryResponse } from "@/types/travel";
import { formatTime, formatDate, isSameDay, calculateLayoverDuration, isLongLayover, isOvernightFlight, formatDurationString } from "@/lib/utils";
import { FlightDetailsTimeline } from "./flight-details-timeline";
import { FareSelector } from "./fare-selector";
import { BookingConfirmation } from "./booking-confirmation";
import { useSubscriptionRules } from "@/hooks/useSubscriptionRules";

interface ExpandableFlightCardProps {
  flight: FlightResult;
  onSelect: (flight: FlightResult) => void;
  isExpanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  itinerary?: ItineraryResponse;
  /**
   * VOY-487: when true, render the gold "BEST MATCH FOR YOU" pill +
   * tinted background variant. Parents typically pass `index === 0`
   * for the top-recommended result.
   */
  featured?: boolean;
}

export function ExpandableFlightCard({
  flight,
  onSelect,
  isExpanded: controlledExpanded,
  onExpandChange,
  itinerary,
  featured = false,
}: ExpandableFlightCardProps) {
  const { addToCart } = useCart();
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [selectedFare, setSelectedFare] = useState<FareOption | null>(null);
  const [bookingStep, setBookingStep] = useState<'details' | 'fare' | 'confirm'>('details');
  const [logoFailed, setLogoFailed] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const setIsExpanded = onExpandChange || setInternalExpanded;

  const isMultiSlice = (flight as MultiSliceFlight).type === "multi-slice";
  const firstSlice = isMultiSlice
    ? (flight as MultiSliceFlight).slices[0]
    : (flight as Flight);
  const price = isMultiSlice
    ? (flight as MultiSliceFlight).totalPrice
    : (flight as Flight).price;

  const formattedDeparture = formatTime(firstSlice.departureTime);
  const formattedArrival = formatTime(firstSlice.arrivalTime);
  const arrivesNextDay = !isSameDay(firstSlice.departureTime, firstSlice.arrivalTime);

  const stops = (firstSlice as Flight).stops ?? 0;
  const preferenceMatch = flight.preferenceMatch;

  // Generate enhanced flight details from API data (memoized for performance)
  const flightDetails = useMemo((): FlightDetails => {
    if (isMultiSlice) {
      const multiSliceFlight = flight as MultiSliceFlight;

      if (multiSliceFlight.slices.length === 2) {
        const outboundSlice = multiSliceFlight.slices[0];
        const returnSlice = multiSliceFlight.slices[1];

        const processSliceDetails = (slice: FlightSlice) => {
          if (slice.segments && slice.segments.length > 1) {
            const layovers = slice.segments.slice(0, -1).map((segment, index) => {
              const nextSegment = slice.segments![index + 1];
              const layoverDuration = calculateLayoverDuration(segment.arrivalTime, nextSegment.departureTime);
              return {
                airport: segment.arrivalAirport,
                duration: layoverDuration,
                isLongLayover: isLongLayover(segment.arrivalTime, nextSegment.departureTime)
              };
            });
            const hasOvernightFlight = slice.segments.some(segment =>
              isOvernightFlight(segment.departureTime, segment.arrivalTime)
            );
            return { segments: slice.segments, layovers, duration: slice.duration, isOvernightFlight: hasOvernightFlight };
          } else {
            const segment = {
              flightNumber: slice.flightNumber, airline: slice.airline,
              departureAirport: slice.origin, departureTime: slice.departureTime,
              arrivalAirport: slice.destination, arrivalTime: slice.arrivalTime,
              duration: slice.duration, departureTerminal: "TBD", arrivalTerminal: "TBD"
            };
            return { segments: [segment], duration: slice.duration, isOvernightFlight: isOvernightFlight(slice.departureTime, slice.arrivalTime) };
          }
        };

        const outboundDetails = processSliceDetails(outboundSlice);
        const returnDetails = processSliceDetails(returnSlice);

        return {
          segments: [...outboundDetails.segments, ...returnDetails.segments],
          totalDuration: `${formatDurationString(outboundSlice.duration)} + ${formatDurationString(returnSlice.duration)}`,
          co2Emissions: "125 kg",
          isOvernightFlight: outboundDetails.isOvernightFlight || returnDetails.isOvernightFlight,
          outbound: outboundDetails, return: returnDetails, isRoundTrip: true
        };
      } else {
        const allSegments: FlightSegment[] = [];
        multiSliceFlight.slices.forEach(slice => {
          if (slice.segments && slice.segments.length > 0) {
            allSegments.push(...slice.segments);
          } else {
            allSegments.push({
              flightNumber: slice.flightNumber, airline: slice.airline,
              departureAirport: slice.origin, departureTime: slice.departureTime,
              arrivalAirport: slice.destination, arrivalTime: slice.arrivalTime,
              duration: slice.duration, departureTerminal: "TBD", arrivalTerminal: "TBD"
            });
          }
        });

        const layovers = allSegments.length > 1 ? allSegments.slice(0, -1).map((segment, index) => {
          const nextSegment = allSegments[index + 1];
          const layoverDuration = calculateLayoverDuration(segment.arrivalTime, nextSegment.departureTime);
          return { airport: segment.arrivalAirport, duration: layoverDuration, isLongLayover: isLongLayover(segment.arrivalTime, nextSegment.departureTime) };
        }) : undefined;

        const hasOvernightFlight = allSegments.some(segment => isOvernightFlight(segment.departureTime, segment.arrivalTime));

        return {
          segments: allSegments, layovers,
          totalDuration: multiSliceFlight.slices.reduce((acc, slice) => acc || slice.duration, ''),
          co2Emissions: "125 kg", isOvernightFlight: hasOvernightFlight
        };
      }
    } else {
      const singleFlight = flight as Flight;

      if (singleFlight.segments && singleFlight.segments.length > 1) {
        const layovers = singleFlight.segments.slice(0, -1).map((segment, index) => {
          const nextSegment = singleFlight.segments![index + 1];
          const layoverDuration = calculateLayoverDuration(segment.arrivalTime, nextSegment.departureTime);
          return { airport: segment.arrivalAirport, duration: layoverDuration, isLongLayover: isLongLayover(segment.arrivalTime, nextSegment.departureTime) };
        });
        const hasOvernightFlight = singleFlight.segments.some(segment => isOvernightFlight(segment.departureTime, segment.arrivalTime));
        return { segments: singleFlight.segments, layovers, totalDuration: singleFlight.duration, co2Emissions: "125 kg", isOvernightFlight: hasOvernightFlight };
      } else {
        const hasOvernightFlight = isOvernightFlight(singleFlight.departureTime, singleFlight.arrivalTime);
        return {
          segments: [{
            flightNumber: singleFlight.flightNumber, airline: singleFlight.airline,
            departureAirport: singleFlight.origin, departureTime: singleFlight.departureTime,
            arrivalAirport: singleFlight.destination, arrivalTime: singleFlight.arrivalTime,
            duration: singleFlight.duration, departureTerminal: "TBD", arrivalTerminal: "TBD"
          }],
          totalDuration: singleFlight.duration, co2Emissions: "125 kg", isOvernightFlight: hasOvernightFlight
        };
      }
    }
  }, [flight, isMultiSlice]);

  // Use the same async config that PricingDisplay uses — avoids race with cold cache
  const { config: flightConfig } = useSubscriptionRules({ product: 'flight' });
  const txFee = 1 + flightConfig.transactionFeePercentage; // 1.04

  // VOY-395: real fare options from the backend (Duffel-derived). The backend
  // applies VOY-346 markup but NOT the transaction fee — the fee is a frontend
  // concern applied via PricingDisplay / txFee. Both branches below apply txFee
  // so the fare selector prices match the card header (PricingDisplay). Without
  // this, fares show ~4% lower than the header price for the same flight.
  const fareOptions: FareOption[] = useMemo(() => {
    const fromBackend = (flight as Flight | MultiSliceFlight).fareOptions;
    if (fromBackend && fromBackend.length > 0) {
      return fromBackend.map(fare => ({
        ...fare,
        price: Math.round(fare.price * txFee * 100) / 100,
      }));
    }

    return [{
      offerId: (flight as Flight | MultiSliceFlight).offerId || flight.id,
      name: 'Standard',
      type: 'standard',
      price: Math.round(price * txFee * 100) / 100,
      currency: 'USD',
      included: {
        changePolicy: 'Changes not reported',
        refundPolicy: 'Refunds not reported',
        carryOnBags: '1 personal item',
        checkedBags: 'Not reported',
        seatSelection: 'Available at checkout',
      },
    }];
  }, [flight, price, txFee]);

  // Single-fare path: when Duffel returns only one fare for an itinerary (the
  // common case in sandbox and for many carriers), skip the fare selector
  // step and jump straight to confirmation with the only fare pre-selected.
  const isSingleFare = fareOptions.length === 1;

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    setIsExpanded(!isExpanded);
    if (!isExpanded) { setBookingStep('details'); setSelectedFare(null); }
  };

  const handleFareSelect = (fare: FareOption) => { setSelectedFare(fare); setBookingStep('confirm'); };

  // Advances from 'details' to the next step. When only one fare is available,
  // skip the fare selector entirely and go straight to confirmation with that
  // fare pre-selected — the user's only choice would have been a no-op.
  const handleProceedFromDetails = () => {
    if (isSingleFare) {
      setSelectedFare(fareOptions[0]);
      setBookingStep('confirm');
    } else {
      setBookingStep('fare');
    }
  };

  const handleAddToCart = () => {
    if (selectedFare && !isAddingToCart) {
      setIsAddingToCart(true);
      try {
        // Cart + checkout must book the specific fare the user picked — use
        // selectedFare.offerId (the Duffel offer id for this fare class), not
        // the card-level offerId (which points to the cheapest fare in the group).
        const flightWithFare = {
          ...flight,
          selectedFare,
          price: selectedFare.price,
          totalPrice: selectedFare.price,
          duffelOfferId: selectedFare.offerId,
        };
        addToCart(flightWithFare as FlightResult, 'flight', itinerary);
        setIsExpanded(false); setBookingStep('details'); setSelectedFare(null);
      } finally { setTimeout(() => setIsAddingToCart(false), 500); }
    }
  };

  const handleCheckout = () => { if (selectedFare) { handleAddToCart(); onSelect(flight); } };

  const resolveAirlineLogo = () => {
    const slice = firstSlice as (Flight & FlightSlice & { ownerLogoSymbolUrl?: string; ownerLogoLockupUrl?: string });
    const flightData = flight as Flight & MultiSliceFlight & { ownerLogoSymbolUrl?: string; ownerLogoLockupUrl?: string };
    return (
      slice.airlineInfo?.logo_symbol_url || slice.ownerLogoSymbolUrl || slice.airlineInfo?.logo_lockup_url ||
      flightData.ownerLogoSymbolUrl || flightData.ownerLogoLockupUrl ||
      flightData.airlineInfo?.logo_symbol_url || flightData.airlineInfo?.logo_lockup_url || null
    );
  };

  const airlineLogo = resolveAirlineLogo();

  useEffect(() => { setLogoFailed(false); }, [airlineLogo, firstSlice.airline]);

  // VOY-513 (D4): single markup application point. The backend
  // (duffelClient.mapDuffelGroupToFlight) already bakes the flight markup into
  // `standardPrice` (= supplier × (1 + markup)) and leaves `specialPrice` = supplier
  // (no markup). The card previously IGNORED those fields and re-derived the standard
  // price via calculatePricing(price, {markup}, …) — applying the markup a SECOND time
  // (invisible only because flight markup is 0 today). Consume the backend fields directly
  // and apply just the transaction fee (the frontend's documented half of the split — see
  // the fareOptions/txFee note above). Markup now lives only in the backend.
  // We always show the non-member standard as the strikethrough; at markup 0 std === member
  // so showSavings naturally falls to false.
  const flightPricing = flight as Flight | MultiSliceFlight;
  const backendStandard = flightPricing.standardPrice ?? price;
  const backendSpecial = flightPricing.specialPrice ?? flightPricing.originalPrice ?? price;
  const standardPriceNum = Math.round(backendStandard * txFee * 100) / 100;
  const memberPriceNum = Math.round(backendSpecial * txFee * 100) / 100;
  const savingsNum = Math.round((standardPriceNum - memberPriceNum) * 100) / 100;
  const showSavings = savingsNum > 0.005;
  const fmtMoney = (n: number) => `$${Math.round(n).toLocaleString()}`;

  // Module-local-ish formatters (cheap; no need to memoize)
  const formatStops = (n: number) => (n === 0 ? 'Direct' : `${n} stop${n > 1 ? 's' : ''}`);

  const airlineInitials = (firstSlice.airline || 'XX').substring(0, 2).toUpperCase();
  const stopsLabel = formatStops(stops);
  const cabinLabel = (firstSlice.cabinClass || 'Economy')
    .toString()
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  // VOY-487 review: derive the carry-on label from the first fare's
  // `included.carryOnBags` rather than hardcoding "1 carry-on". For
  // Basic Economy fares this can be "1 personal item" (no carry-on).
  // Falls back to the cabin label alone when no fare data is available.
  const carryOnLabel = fareOptions[0]?.included?.carryOnBags ?? null;
  const fareSummary = carryOnLabel ? `${cabinLabel} · ${carryOnLabel}` : cabinLabel;

  // VOY-487 review: precompute the return-slice view data instead of
  // running an IIFE inside JSX (only relevant for two-slice itineraries,
  // i.e. classic round-trip).
  const returnSliceView = useMemo(() => {
    if (!isMultiSlice) return null;
    const slices = (flight as MultiSliceFlight).slices;
    if (slices.length !== 2) return null;
    const returnSlice = slices[1];
    const returnStops = (returnSlice as FlightSlice & { stops?: number }).stops ?? 0;
    return {
      dep: formatTime(returnSlice.departureTime),
      arr: formatTime(returnSlice.arrivalTime),
      depDate: formatDate(returnSlice.departureTime),
      arrDate: formatDate(returnSlice.arrivalTime),
      arrivesNextDay: !isSameDay(returnSlice.departureTime, returnSlice.arrivalTime),
      origin: returnSlice.origin,
      destination: returnSlice.destination,
      durText: `${formatDurationString(returnSlice.duration)} · ${formatStops(returnStops)}`,
    };
  }, [flight, isMultiSlice]);

  const handleSelectCta = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(true);
    setBookingStep('details');
    setSelectedFare(null);
  };

  // VOY-487 review: keyboard expand/collapse must match `handleCardClick`
  // semantics — reset bookingStep + selectedFare when expanding so a
  // keyboard user who collapsed at 'confirm' doesn't land back on the
  // confirmation step with a stale fare on re-expand.
  const handleKeyToggle = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    const willOpen = !isExpanded;
    setIsExpanded(willOpen);
    if (willOpen) {
      setBookingStep('details');
      setSelectedFare(null);
    }
  };

  return (
    <article
      className={`flight-card${featured ? ' featured' : ''}`}
      onClick={handleCardClick}
      tabIndex={0}
      aria-expanded={isExpanded}
      aria-label={`${firstSlice.airline} ${firstSlice.origin} to ${firstSlice.destination}, ${fmtMoney(memberPriceNum)} as member`}
      onKeyDown={handleKeyToggle}
      style={{ cursor: 'pointer' }}
    >
      {featured && <span className="best-pill">BEST MATCH FOR YOU</span>}

      {/* Column 1: airline */}
      <div className="fc-l">
        {airlineLogo && !logoFailed ? (
          <img
            src={airlineLogo}
            alt={`${firstSlice.airline} logo`}
            className="airline-logo"
            style={{ background: 'transparent', border: 0, objectFit: 'contain', padding: 4 }}
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <div className="airline-logo">{airlineInitials}</div>
        )}
        <div>
          <div className="airline-name">{firstSlice.airline}</div>
          <div className="fc-lbl">{fareSummary}</div>
        </div>
      </div>

      {/* Column 2: route */}
      <div className="fc-route">
        <div className="time-block">
          <div className="time">{formattedDeparture}</div>
          <div className="code">{firstSlice.origin}</div>
        </div>
        <div className="dur-block">
          <div className="dur-line">
            <span className="dot-l" />
            <span className="dur-bar" />
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="fc-plane">
              <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1L15 22v-1.5L13 19v-5.5l8 2.5z" />
            </svg>
            <span className="dur-bar" />
            <span className="dot-l" />
          </div>
          <div className="dur-text">
            {formatDurationString(firstSlice.duration)} · {stopsLabel}
          </div>
        </div>
        <div className="time-block">
          <div className="time">
            {formattedArrival}
            {arrivesNextDay && <span className="next-day">+1</span>}
          </div>
          <div className="code">{firstSlice.destination}</div>
        </div>
      </div>

      {/* Column 3: price + CTA */}
      <div className="fc-price">
        <div className="price-pair">
          {showSavings && <span className="price-std">{fmtMoney(standardPriceNum)}</span>}
          <span className="price-mem">{fmtMoney(memberPriceNum)}</span>
        </div>
        {showSavings && (
          <div className="save-tag">− {fmtMoney(savingsNum)} as member</div>
        )}
        <button
          type="button"
          className="select-cta"
          onClick={handleSelectCta}
          disabled={isExpanded}
          aria-label={isExpanded ? 'Already selected, see fare options below' : 'Select this flight'}
        >
          {isExpanded ? 'Selected ✓' : 'Select'}
        </button>
      </div>

      {/* Expanded booking flow — preserved from original */}
      {isExpanded && (
        <div className="fc-expanded" onClick={(e) => e.stopPropagation()}>
          <Separator className="mb-6" />

          {/* Return slice summary (multi-slice only) — kept simple to avoid
              duplicating the FlightDetailsTimeline which already renders
              full per-segment detail inside the 'details' step below. */}
          {returnSliceView && (
            <div className="fc-route fc-route-return">
              <div className="time-block">
                <div className="time">{returnSliceView.dep}</div>
                <div className="code">{returnSliceView.origin}</div>
                <div className="fc-lbl fc-lbl-stacked">{returnSliceView.depDate}</div>
              </div>
              <div className="dur-block">
                <div className="dur-line">
                  <span className="dot-l" />
                  <span className="dur-bar" />
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="fc-plane">
                    <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1L15 22v-1.5L13 19v-5.5l8 2.5z" />
                  </svg>
                  <span className="dur-bar" />
                  <span className="dot-l" />
                </div>
                <div className="dur-text">{returnSliceView.durText}</div>
              </div>
              <div className="time-block">
                <div className="time">
                  {returnSliceView.arr}
                  {returnSliceView.arrivesNextDay && <span className="next-day">+1</span>}
                </div>
                <div className="code">{returnSliceView.destination}</div>
                <div className="fc-lbl fc-lbl-stacked">{returnSliceView.arrDate}</div>
              </div>
            </div>
          )}

          {/* Preference Matches (kept from original) */}
          {preferenceMatch?.matches && preferenceMatch.matches.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {preferenceMatch.matches.map((match, index) => (
                <span key={index} className="text-xs px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 font-medium">
                  {match}
                </span>
              ))}
            </div>
          )}

          {bookingStep === 'details' && (
            <div className="space-y-6">
              <FlightDetailsTimeline details={flightDetails} />
              <div className="flex justify-end">
                <Button onClick={handleProceedFromDetails} className="flex items-center gap-2 rounded-full">
                  {isSingleFare ? 'Continue' : 'Select Fare'} <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {bookingStep === 'fare' && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <Button variant="ghost" size="sm" onClick={() => setBookingStep('details')} className="rounded-full">← Back</Button>
                <h3 className="font-semibold">Select Your Fare</h3>
              </div>
              <FareSelector fareOptions={fareOptions} onSelect={handleFareSelect} />
            </div>
          )}

          {bookingStep === 'confirm' && selectedFare && (
            <div className="space-y-6">
              <div className="flex items-center gap-2 mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Single-fare cards skip the 'fare' step entirely, so the
                    // back button jumps back to 'details' rather than looping
                    // through auto-selection.
                    setBookingStep(isSingleFare ? 'details' : 'fare');
                    setSelectedFare(null);
                  }}
                  className="rounded-full"
                >← Back</Button>
                <h3 className="font-semibold">Confirm Your Selection</h3>
              </div>
              <BookingConfirmation flight={flight} selectedFare={selectedFare} flightDetails={flightDetails} onAddToCart={handleAddToCart} onCheckout={handleCheckout} />
            </div>
          )}
        </div>
      )}
    </article>
  );
}
